import { useFetch } from "~vue/api";
import type { ErrorResponse, TeamInvitationJoinsResult, TeamJoinRequest, TeamJoinResult } from "~vue/api/generated/team-invitations";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";

import { TEAM_INTERACTION_SOURCE, teamInteractionRequest, type TeamInteractionSource } from "./teamInteraction";
import { clearTeamJoin, markInvitationsChecked } from "./teamJoinContinuation";

type TeamInvitationResult = { status: "joined"; teamUrl: string } | { status: "unavailable" } | { status: "onboarding_required" } | { status: "failed" };

export async function acceptEmailInvitations(key: string, source: TeamInteractionSource = TEAM_INTERACTION_SOURCE.TEAM_INVITATION): Promise<TeamInvitationJoinsResult | null> {
    const { data, error } = await useFetch("/v1/team-join/invitations", teamInteractionRequest(source)).post().json<TeamInvitationJoinsResult>();
    if (error.value || !data.value) {
        return null;
    }
    markInvitationsChecked(key);
    return data.value;
}

export async function acceptTeamInvitation(
    invitation: TeamJoinRequest,
    key: string,
    source: TeamInteractionSource = TEAM_INTERACTION_SOURCE.TEAM_INVITATION,
): Promise<TeamInvitationResult> {
    void logUserInteraction(AmplitudeEventNames.TEAM_JOIN_STARTED, { source, entry_point: source });
    const { data, error, statusCode } = await useFetch("/v1/team-join", teamInteractionRequest(source)).post(invitation).json<TeamJoinResult | ErrorResponse>();
    if (data.value && "code" in data.value) {
        if (statusCode.value === 409 && data.value.code === "team_join_onboarding_required") {
            return { status: "onboarding_required" };
        }
        if (statusCode.value === 404 && data.value.code === "team_join_unavailable") {
            clearTeamJoin(invitation.request_id);
            return { status: "unavailable" };
        }
    }
    if (error.value || !data.value || !("team_url" in data.value) || !(await acceptEmailInvitations(key, source))) {
        return { status: "failed" };
    }
    clearTeamJoin(invitation.request_id);
    return { status: "joined", teamUrl: data.value.team_url };
}
